import axios from "axios";
import type { TenantAccount } from "@/zustand/useTenantStore";

/**
 * Central Auth Service — communicates with /api/central-auth/ endpoints.
 * These endpoints are always on the main gateway URL, not on tenant-specific APIs.
 */

const centralAuthBaseUrl =
  import.meta.env.VITE_CENTRAL_AUTH_URL || import.meta.env.VITE_APP_BASE_URL;

const centralAxios = axios.create({
  baseURL: centralAuthBaseUrl,
});

export interface CentralLoginResponse {
  gateway_token: string;
  accounts: TenantAccount[];
  email: string;
}

export interface SelectTenantResponse {
  access: string;
  refresh: string;
  tenant: {
    id: string;
    name: string;
    slug: string;
    api_url: string;
    role: string;
  };
}

export interface RefreshResponse {
  access: string;
  refresh: string;
}

export const centralLogin = async (
  email: string,
  password: string,
): Promise<CentralLoginResponse> => {
  const response = await centralAxios.post<CentralLoginResponse>(
    "central-auth/login/",
    { email, password },
  );
  return response.data;
};

export const centralGoogleLogin = async (
  accessToken: string,
): Promise<CentralLoginResponse> => {
  const response = await centralAxios.post<CentralLoginResponse>(
    "central-auth/google/",
    { access_token: accessToken },
  );
  return response.data;
};

export const selectTenant = async (
  gatewayToken: string,
  tenantId: string,
): Promise<SelectTenantResponse> => {
  const response = await centralAxios.post<SelectTenantResponse>(
    "central-auth/select-tenant/",
    { tenant_id: tenantId },
    {
      headers: { Authorization: `Bearer ${gatewayToken}` },
    },
  );
  return response.data;
};

export const refreshTenantToken = async (
  refreshToken: string,
): Promise<RefreshResponse> => {
  const response = await centralAxios.post<RefreshResponse>(
    "central-auth/refresh/",
    { refresh: refreshToken },
  );
  return response.data;
};

export const getMyAccounts = async (
  token: string,
): Promise<{ accounts: TenantAccount[]; email: string }> => {
  const response = await centralAxios.get("central-auth/my-accounts/", {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const requestAccess = async (
  token: string,
  tenantId: string,
  message: string,
): Promise<{ detail: string }> => {
  const response = await centralAxios.post(
    "central-auth/request-access/",
    { tenant: tenantId, message },
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return response.data;
};
